import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Calendar, Eye } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

/**
 * Interface representing the props for the QuestionBody component.
 *
 * - views - The number of views the question has received.
 * - text - The content of the question, which may contain hyperlinks.
 * - askby - The username of the user who asked the question.
 * - meta - Additional metadata related to the question, such as the date and time it was asked.
 */
interface QuestionBodyProps {
  views: number;
  text: string;
  askby: string;
  meta: string;
}

/**
 * QuestionBody component that displays the body of a question.
 * It includes the number of views, the question content (with hyperlink handling),
 * the username of the author, and additional metadata.
 *
 * @param views The number of views the question has received.
 * @param text The content of the question.
 * @param askby The username of the question's author.
 * @param meta Additional metadata related to the question.
 */
const QuestionBody = ({ views, text, askby, meta }: QuestionBodyProps) => (
  <div className='p-4 bg-gray-50 rounded-md border border-gray-200'>
    <div className='flex items-start gap-3'>
      <div className='h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary'>
        <MessageSquare size={18} />
      </div>

      <div className='flex-1'>
        <div className='flex flex-wrap justify-between gap-2 mb-1'>
          <Link to={`/user/${askby}`} className='font-medium text-blue-600 hover:underline'>
            {askby}
          </Link>

          <div className='flex items-center text-sm text-muted-foreground'>
            <Calendar size={14} className='mr-1' />
            {meta}
          </div>
        </div>

        <div className='mb-4'>
          <span className='text-sm text-muted-foreground'>asked a question</span>
        </div>

        <div className='mt-4 prose prose-sm max-w-none'>
          <ReactMarkdown>{text}</ReactMarkdown>
        </div>

        <div className='mt-3 flex items-center text-sm text-muted-foreground'>
          <div className='flex items-center'>
            <Eye size={14} className='mr-1' />
            {views} view{views !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default QuestionBody;
